import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';

type InventoryInformation = {
  inventoryPolicy: string;
  inventoryQuantity: number;
  sellableOnlineQuantity: number;
  inventoryManagement: string;
};

type Inventory = Record<string, InventoryInformation>;
type UpdateInventory = (inventory: Inventory) => void;

const InventoryContext = createContext<
  { inventory: Inventory; updateInventory: UpdateInventory } | undefined
>(undefined);

const inventoryReducer = (state, action) => {
  switch (action.type) {
    case 'update': {
      return { inventory: { ...state.inventory, ...action.inventory } };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const InventoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(inventoryReducer, { inventory: null });
  const updateInventory = useCallback(
    (inventory) => dispatch({ type: 'update', inventory }),
    [dispatch],
  );
  const inventory: Inventory = useMemo(
    () => state.inventory,
    [state.inventory],
  );
  const value = { inventory, updateInventory };
  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

const useInventory = () => {
  const context = useContext(InventoryContext);
  if (context == undefined) {
    throw new Error('useInventory must be used within a InventoryProvider');
  }
  return context;
};

export { InventoryProvider, useInventory };
